import React, { FC, useContext } from 'react';
import {
  AddressType,
  GetPolicyByVersionIdResponse,
  LienholderType,
  VehicleOptionsResponse,
  VehicleType,
  VersionedObjectDiff,
} from '@clearcover/core-app-types';
import { Container } from '@clearcover/atlas';
import { CardSectionInfoPairs, LoadingPage } from 'Shared/components';
import styles from './styles.module.css';
import {
  booleanValueMapping,
  EMDASH,
  formatAddress,
  formatNumberWithCommas,
  isPresent,
} from 'Shared/utils';
import { MultilineAddress } from 'Policies/components/Address';
import { useApi, useNewApi } from 'Shared/services/Api';
import { useParams } from 'react-router-dom';
import { areSameAddress } from 'Policies/utils/helpers';
import { formatDiff } from 'Policies/components/PolicyActivities/Common/Common';
import { PolicyContext } from 'Policies/utils';
import { env } from 'Shared/constants/env';

type VehicleDetailsDiff = {
  diff: VersionedObjectDiff<VehicleType>;
  versionId: string;
};

export const PolicyChangeVehicleDetails: FC<VehicleDetailsDiff> = ({
  diff,
  versionId,
}) => {
  const { details } = useContext(PolicyContext);
  const { id: policyId } = useParams<{
    id: string;
  }>();
  const [{ data, loading }] = useNewApi<GetPolicyByVersionIdResponse>(
    {
      url: `/policies/${policyId}/version/${versionId}`,
    },
    true,
    false
  );

  const [{ data: vehicleOptionsData, loading: vehicleOptionsLoading }] = useApi<
    VehicleOptionsResponse
  >(
    `/options/vehicle?identifier=${details.filingIdentifier}&region=${details.region}`
  );

  if (loading || vehicleOptionsLoading) {
    return <LoadingPage />;
  }

  const VehicleToShowDefault =
    diff.type === 'ADDED' || diff.type === 'UPDATED' ? diff.new : diff.old;
  const vehicleToDiff =
    diff.type === 'UPDATED' ? diff.old : VehicleToShowDefault;

  const mailingAddress = data?.policyDetails.addresses.mailing;
  const vehicleOptionsQuestionsConfig =
    vehicleOptionsData?.vehicleOptionsQuestionsConfig;

  // Vehicle Info section - conditional headers and data
  const vehicleInfoHeaders = ['VIN', 'Year', 'Make', 'Model', 'Body Style'];
  const vehicleInfoData = [
    formatDiff(
      vehicleToDiff.vin.toUpperCase(),
      VehicleToShowDefault.vin.toUpperCase()
    ),
    formatDiff(vehicleToDiff.year, VehicleToShowDefault.year),
    formatDiff(vehicleToDiff.make, VehicleToShowDefault.make),
    formatDiff(vehicleToDiff.model, VehicleToShowDefault.model),
    formatDiff(vehicleToDiff.bodyStyle, VehicleToShowDefault.bodyStyle),
  ];

  // Anti-theft device question (new style) - shown when askForAntiTheftDevice is true
  if (vehicleOptionsQuestionsConfig?.askForAntiTheftDevice) {
    vehicleInfoHeaders.push('Vehicle equipped with anti-theft device');
    vehicleInfoData.push(
      formatDiff(
        formatYesNo(vehicleToDiff.antiTheft.hasAntiTheftDevice),
        formatYesNo(VehicleToShowDefault.antiTheft.hasAntiTheftDevice)
      )
    );
  } else {
    // Anti-theft features checkboxes (old style) - shown when askForAntiTheftDevice is false/undefined
    vehicleInfoHeaders.push('Anti-Theft Features');
    vehicleInfoData.push(
      formatDiff(
        antiTheftString(vehicleToDiff.antiTheft),
        antiTheftString(VehicleToShowDefault.antiTheft)
      )
    );
  }

  // Details section - conditional headers and data
  const detailsHeaders: string[] = [];
  const detailsData: ReturnType<typeof formatDiff>[] = [];

  // Avg Miles - conditional based on askForAvgMilesPerYear
  if (vehicleOptionsQuestionsConfig?.askForAvgMilesPerYear !== false) {
    detailsHeaders.push('Avg Miles Driven Per Year');
    detailsData.push(
      formatDiff(
        formatWithCommas(vehicleToDiff.usage.yearlyMileage),
        formatWithCommas(VehicleToShowDefault.usage.yearlyMileage)
      )
    );
  }

  // Rideshare - always shown
  detailsHeaders.push('Rideshare');
  detailsData.push(
    formatDiff(
      formatYesNo(vehicleToDiff.usage.rideShare),
      formatYesNo(VehicleToShowDefault.usage.rideShare)
    )
  );

  // Business use questions OR Primary Use - conditional based on askForBusinessUseType
  if (vehicleOptionsQuestionsConfig?.askForBusinessUseType) {
    detailsHeaders.push('Is used for any business purposes?');
    detailsData.push(
      formatDiff(
        formatYesNo(vehicleToDiff.usage.business),
        formatYesNo(VehicleToShowDefault.usage.business)
      )
    );

    detailsHeaders.push('Business use type');
    detailsData.push(
      formatDiff(
        formatBusinessUseType(vehicleToDiff.usage.unacceptableBusinessUse),
        formatBusinessUseType(
          VehicleToShowDefault.usage.unacceptableBusinessUse
        )
      )
    );
  } else {
    detailsHeaders.push('Primary Use');
    detailsData.push(
      formatDiff(vehicleToDiff.usage.use, VehicleToShowDefault.usage.use)
    );
  }

  // Registered to applicant/spouse - conditional based on askForRegisteredToPolicyholderOrSpouse
  if (vehicleOptionsQuestionsConfig?.askForRegisteredToPolicyholderOrSpouse) {
    detailsHeaders.push(
      'Is applicant or spouse the registered owner or lessee?'
    );
    detailsData.push(
      formatDiff(
        booleanValueMapping[
          vehicleToDiff.registeredToPolicyholderOrSpouse?.toString() || ''
        ],
        booleanValueMapping[
          VehicleToShowDefault.registeredToPolicyholderOrSpouse?.toString() ||
            ''
        ]
      )
    );
  }

  // Ownership Status - always shown
  detailsHeaders.push('Ownership Status');
  detailsData.push(
    formatDiff(vehicleToDiff.ownerShipType, VehicleToShowDefault.ownerShipType)
  );

  // Lienholder - always shown
  detailsHeaders.push('Lienholder Name');
  detailsData.push(
    formatDiff(
      vehicleToDiff.lienholder.name,
      VehicleToShowDefault.lienholder.name
    )
  );

  detailsHeaders.push('Lienholder Address');
  detailsData.push(
    formatDiff(
      toMultiLineAddress(vehicleToDiff),
      toMultiLineAddress(VehicleToShowDefault)
    )
  );

  // Usage details - conditional based on askForUsageDetails AND env.vehicleNewQuestionsEnabled
  if (
    vehicleOptionsQuestionsConfig?.askForUsageDetails &&
    env.vehicleNewQuestionsEnabled
  ) {
    const usageDetailsLabels: Record<string, string> = {
      usedInMexico: 'Used in Mexico',
      availableForNonCoveredDrivers: 'Available for non-covered drivers',
      emergencyServices: 'Used for emergency services',
      feeBasedServices: 'Used for fee-based services',
      hazmatToolTransport: 'Used for hazmat/tool transport',
      usedAsResidence: 'Used as residence',
    };

    Object.entries(usageDetailsLabels).forEach(([key, label]) => {
      const oldValue =
        vehicleToDiff.usageDetails?.[
          key as keyof typeof vehicleToDiff.usageDetails
        ];
      const newValue =
        VehicleToShowDefault.usageDetails?.[
          key as keyof typeof VehicleToShowDefault.usageDetails
        ];
      if (oldValue !== undefined || newValue !== undefined) {
        detailsHeaders.push(label);
        detailsData.push(
          formatDiff(
            formatOptionalYesNo(oldValue),
            formatOptionalYesNo(newValue)
          )
        );
      }
    });
  }

  // Garaging Address section - conditional headers and data
  const garagingHeaders = [
    'Garaging Address',
    'Is this vehicle parked at the mailing address?',
  ];
  const garagingData = [
    formatDiff(
      {
        display: <MultilineAddress address={vehicleToDiff.garagingAddress} />,
        value: formatAddress(vehicleToDiff.garagingAddress),
      },
      {
        display: (
          <MultilineAddress address={VehicleToShowDefault.garagingAddress} />
        ),
        value: formatAddress(VehicleToShowDefault.garagingAddress),
      }
    ),
    formatDiff(
      isSameAsMailingAddress(vehicleToDiff.garagingAddress, mailingAddress),
      isSameAsMailingAddress(
        VehicleToShowDefault.garagingAddress,
        mailingAddress
      )
    ),
  ];

  // Parking Location Reason - conditional (only shown when vehicle is NOT parked at mailing address)
  const isOldVehicleAtDifferentAddress =
    mailingAddress &&
    !areSameAddress(vehicleToDiff.garagingAddress, mailingAddress);
  const isNewVehicleAtDifferentAddress =
    mailingAddress &&
    !areSameAddress(VehicleToShowDefault.garagingAddress, mailingAddress);
  const showParkingLocationReason =
    vehicleOptionsQuestionsConfig?.askForParkingLocationReason &&
    env.vehicleNewQuestionsEnabled &&
    (isOldVehicleAtDifferentAddress || isNewVehicleAtDifferentAddress);

  if (showParkingLocationReason) {
    garagingHeaders.push(
      'For what reason is this vehicle garaged at a separate location?'
    );
    garagingData.push(
      formatDiff(
        vehicleToDiff.parkingLocationReason || EMDASH,
        VehicleToShowDefault.parkingLocationReason || EMDASH
      )
    );
  }

  // In-State Parking Duration - conditional
  if (
    vehicleOptionsQuestionsConfig?.askForInstateParkingDuration &&
    env.vehicleNewQuestionsEnabled
  ) {
    garagingHeaders.push(
      `In a typical year, is this vehicle parked in ${details.mailingState} for at least 10 months?`
    );
    garagingData.push(
      formatDiff(
        formatInStateParkingDuration(vehicleToDiff.inStateParkingDuration),
        formatInStateParkingDuration(
          VehicleToShowDefault.inStateParkingDuration
        )
      )
    );
  }

  // Parking Type - conditional
  if (
    vehicleOptionsQuestionsConfig?.askForParkingType &&
    env.vehicleNewQuestionsEnabled
  ) {
    garagingHeaders.push('When not in use, where is this vehicle parked?');
    garagingData.push(
      formatDiff(
        vehicleToDiff.parkingType || EMDASH,
        VehicleToShowDefault.parkingType || EMDASH
      )
    );
  }

  return (
    <div data-testid="vehicle-details">
      <Container>
        <h4>Vehicle Info</h4>
        <CardSectionInfoPairs
          headers={vehicleInfoHeaders}
          data={vehicleInfoData}
          className={styles.details}
        />
      </Container>
      <Container>
        <h4>Details</h4>
        <CardSectionInfoPairs
          headers={detailsHeaders}
          data={detailsData}
          className={styles.details}
        />
      </Container>
      <Container className={styles.garagingAddressContainer}>
        <h4>Garaging Address</h4>
        <CardSectionInfoPairs
          headers={garagingHeaders}
          data={garagingData}
          className={styles.details}
        />
      </Container>
    </div>
  );
};

// TODO terms taken from VehicleInfo.tsx, may want to consolidate into a config of some sort
const toAntiTheftString = (input: string): string => {
  switch (input) {
    case 'activeDisable':
      return 'Active Disable';
    case 'alarm':
      return 'Alarm System';
    case 'passiveDisable':
      return 'Passive Disable';
    case 'recovery':
      return 'Vehicle Recovery';
    default:
      return '';
  }
};

const toAddressType = (lienholder: LienholderType): AddressType => {
  return {
    address1: lienholder.addressLine1,
    address2: lienholder.addressLine2,
    city: lienholder.city,
    state: lienholder.state,
    zip: lienholder.postalCode,
  };
};

const formatWithCommas = (number: number | null): string =>
  number ? formatNumberWithCommas(number) : '';

const formatYesNo = (boolValue: boolean): string => (boolValue ? 'Yes' : 'No');

const antiTheftString = (antiTheft: any): string => {
  const antiTheftString = Object.entries(antiTheft)
    .map(([key, value]) => (value ? toAntiTheftString(key) : undefined))
    .filter(value => value !== undefined)
    .join(', ');
  return antiTheftString ? antiTheftString : EMDASH;
};

const toMultiLineAddress = (vechile: VehicleType): any =>
  vechile.ownerShipType === 'owned'
    ? EMDASH
    : {
        display: (
          <MultilineAddress address={toAddressType(vechile.lienholder)} />
        ),
        value: vechile.lienholder.addressFull,
      };

const isSameAsMailingAddress = (
  garagingAddress: AddressType,
  mailingAddress: AddressType
): string =>
  isPresent(mailingAddress)
    ? formatYesNo(areSameAddress(garagingAddress, mailingAddress))
    : EMDASH;

const formatOptionalYesNo = (value: boolean | null | undefined): string =>
  value === null || value === undefined ? EMDASH : formatYesNo(value);

const formatBusinessUseType = (
  unacceptableBusinessUse: boolean | null | undefined
): string => {
  if (
    unacceptableBusinessUse === null ||
    unacceptableBusinessUse === undefined
  ) {
    return EMDASH;
  }
  return unacceptableBusinessUse ? 'Unacceptable' : 'Acceptable';
};

const formatInStateParkingDuration = (duration: string | null): string => {
  if (!duration) return EMDASH;
  switch (duration) {
    case 'atLeast10Months':
      return 'Yes';
    case 'lessThan10Months':
      return 'No';
    default:
      return duration;
  }
};
